import { COUNTRIES, COUNTRY_TIMEZONE, getTimezone, type TenantTimezone } from '@bbpos/types';
import { FormField, Select } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';

/** The option that keeps the store on the business's timezone (stored as null). */
export const BUSINESS_ZONE = '__business__';

function utcOffset(tz: TenantTimezone): string {
  const h = Math.floor(tz.offsetMin / 60);
  const m = tz.offsetMin % 60;
  return `UTC+${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
}

/**
 * Store timezone choices (T14): the business's own zone first, then one per
 * supported country — the store's country decides its zone.
 */
export function locationTimezoneOptions(businessZone: string | null | undefined) {
  const business = getTimezone(businessZone);
  return [
    { value: BUSINESS_ZONE, label: `Same as business (${business.abbr}, ${utcOffset(business)})` },
    ...COUNTRIES.filter((c) => COUNTRY_TIMEZONE[c.iso]).map((c) => {
      const tz = COUNTRY_TIMEZONE[c.iso];
      return { value: tz.iana, label: `${c.flag} ${c.name} (${tz.abbr}, ${utcOffset(tz)})` };
    }),
  ];
}

interface LocationTimezoneSelectProps {
  id: string;
  /** The store's own zone; null = the business's. */
  value: string | null;
  onChange: (timezone: string | null) => void;
}

/** "Store country & time zone" — shifts, attendance and the till run on this clock. */
export function LocationTimezoneSelect({ id, value, onChange }: LocationTimezoneSelectProps) {
  const businessZone = useAuthStore((s) => (s.profile?.tenant as { timezone?: string } | undefined)?.timezone);
  return (
    <FormField label="Store country & time zone" htmlFor={id}>
      <Select
        id={id}
        options={locationTimezoneOptions(businessZone)}
        value={value ?? BUSINESS_ZONE}
        onChange={(v) => onChange(typeof v === 'string' && v !== BUSINESS_ZONE ? v : null)}
      />
      <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
        Shifts, attendance and the till at this store use this clock. Pick the store&apos;s country if
        it is not where the business is.
      </p>
    </FormField>
  );
}
